import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { NotificationPreferences } from "@/types/settings";

const notificationSchema = z.object({
  booking_updates: z.boolean(),
  payment_confirmation: z.boolean(),
  marketing_emails: z.boolean(),
});

interface NotificationSettingsProps {
  preferences: NotificationPreferences;
  onSubmit: (values: NotificationPreferences) => Promise<void>;
  isLoading: boolean;
}

export const NotificationSettings = ({
  preferences,
  onSubmit,
  isLoading,
}: NotificationSettingsProps) => {
  const form = useForm<NotificationPreferences>({
    resolver: zodResolver(notificationSchema),
    defaultValues: {
      booking_updates: preferences?.booking_updates ?? true,
      payment_confirmation: preferences?.payment_confirmation ?? true,
      marketing_emails: preferences?.marketing_emails ?? false,
    },
  });

  return (
    <Card className="p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <h2 className="text-lg font-semibold mb-4">Notification Preferences</h2>
          
          <FormField
            control={form.control}
            name="booking_updates"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Booking Updates
                  </FormLabel>
                  <FormDescription>
                    Receive notifications about your booking status changes
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="payment_confirmation"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Payment Confirmations
                  </FormLabel>
                  <FormDescription>
                    Get notified about payment updates and confirmations
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="marketing_emails"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Marketing Emails
                  </FormLabel>
                  <FormDescription>
                    Receive updates about new features and promotions
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <Button type="submit" disabled={isLoading}>
            Save Notification Settings
          </Button>
        </form>
      </Form>
    </Card>
  );
};